import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBell, FiArrowRight, FiSettings, FiCheckCircle,
  FiMail, FiSmartphone, FiMonitor
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const NotificationsDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/notifications" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Notifications</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl mb-4">
              <FiBell className="w-6 h-6 text-yellow-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Notifications</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Stay informed about important updates, task assignments, and deadlines with customizable notifications.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Never Miss an Update</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tasklina's notification system keeps you in the loop without overwhelming you. 
                Customize what you receive, how you receive it, and when you receive it.
              </p>
            </div>
          </section>

          {/* Notification Types */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What You'll Be Notified About</h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">Task assigned to you</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">Comments on your tasks</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">Task status changes</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">Upcoming due dates</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">@mentions in comments</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-200">Project and team invitations</span>
              </div>
            </div>
          </section>

          {/* Notification Channels */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notification Channels</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full w-fit mx-auto mb-3">
                  <FiMonitor className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">In-App</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Notification bell shows real-time updates while using Tasklina
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center">
                <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-3">
                  <FiMail className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Email</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Get email digests or instant notifications to your inbox
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center">
                <div className="p-3 bg-purple-100 rounded-full w-fit mx-auto mb-3">
                  <FiSmartphone className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Push</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Browser push notifications even when tab is closed
                </p>
              </div>
            </div>
          </section>

          {/* Managing Notifications */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Managing Notifications</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <FiSettings className="w-5 h-5" />
                <span>Notification Preferences</span>
              </h3>
              <ol className="text-gray-600 dark:text-gray-300 space-y-2 list-decimal list-inside mb-4">
                <li>Go to Settings → Notifications</li>
                <li>Choose notification types you want to receive</li>
                <li>Select channels for each notification type</li>
                <li>Set quiet hours to pause notifications</li>
              </ol>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Email Digest Options</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• <strong>Instant:</strong> Get emails as events happen</li>
                  <li>• <strong>Hourly:</strong> Bundled digest every hour</li>
                  <li>• <strong>Daily:</strong> One summary email per day</li>
                  <li>• <strong>Weekly:</strong> Weekly recap on Mondays</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Notification Center */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notification Center</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Access all your notifications from the bell icon in the top navigation.
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Unread count:</strong> Badge shows number of unread notifications</span>
                </li>
                <li className="flex items-start space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Quick actions:</strong> Mark as read or take action directly</span>
                </li>
                <li className="flex items-start space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Filter:</strong> View all, unread, or by type</span>
                </li>
                <li className="flex items-start space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Mark all read:</strong> Clear all notifications at once</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/calendar"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Calendar</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default NotificationsDocs;
